import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import {
  type BulkResendPreviewResponse,
  type BulkResendResult,
  useBulkResend,
  useBulkResendPreview,
} from "hooks/use-delivery-series-api";
import { cn } from "lib/utils";
import { type FC, useCallback, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toastError } from "utils/toast-utils";
import { BulkResendSeriesItem } from "./BulkResendSeriesItem";
import {
  type BulkResendDraft,
  buildResendItems,
  classifyBulkResendError,
  describeResult,
  duplicatedRecipientsNotice,
  PREVIEW_FAILED_MESSAGE,
  quotaMessage,
  REJECTED_MESSAGE,
  resultHeadline,
  UNCONFIRMED_NOTE,
  UNKNOWN_MESSAGE,
  UNKNOWN_TITLE,
} from "./bulk-resend-logic";

/** 確認に戻したときに出すエラー. quota は閉じるまで送信できない */
type ConfirmFailure =
  | { kind: "quota"; remaining: number }
  | { kind: "rejected" };

type Phase =
  | { kind: "loading" }
  | { kind: "confirm"; failure: ConfirmFailure | null }
  | { kind: "running" }
  | { kind: "result"; results: BulkResendResult[] }
  | { kind: "unknown" };

export type BulkResendModalProps = {
  /** 選んだシリーズ ID (一覧の順) */
  seriesIds: string[];
  /** 送信前に閉じた (選択は残す) */
  onCancel: () => void;
  /** 結果 / 結果不明になった (一覧を裏で取り直す) */
  onSettled: () => void;
  /** 結果 / 結果不明を閉じた (選択を解除する) */
  onDone: () => void;
};

/**
 * 複数シリーズの一括再配信モーダル.
 * 読み込み中 → 確認 → 実行中 → 結果 / 結果不明 を 1 つのモーダルで切り替える.
 * Layout のスクロールコンテナの外に出すため body 直下へ portal する.
 * @param {BulkResendModalProps} props 対象とハンドラ
 * @return {JSX.Element} モーダル
 */
export const BulkResendModal: FC<BulkResendModalProps> = ({
  seriesIds,
  onCancel,
  onSettled,
  onDone,
}) => {
  const { preview } = useBulkResendPreview();
  const { resend } = useBulkResend();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [data, setData] = useState<BulkResendPreviewResponse | null>(null);
  const [drafts, setDrafts] = useState<Record<string, BulkResendDraft>>({});
  const titleId = useId();

  useEffect(() => {
    let cancelled = false;
    /**
     * preview を取得し、編集欄を雛型の値で初期化する.
     * 閉じた後に返った応答は捨てる.
     * @return {Promise<void>}
     */
    const loadPreview = async (): Promise<void> => {
      try {
        const response = await preview(seriesIds);
        if (cancelled) return;
        setData(response);
        setDrafts(
          Object.fromEntries(
            response.items.flatMap(item =>
              item.template
                ? [
                    [
                      item.seriesId,
                      {
                        subject: item.template.subject,
                        bodyText: item.template.bodyText,
                      },
                    ],
                  ]
                : [],
            ),
          ),
        );
        setPhase({ kind: "confirm", failure: null });
      } catch {
        if (cancelled) return;
        toastError(PREVIEW_FAILED_MESSAGE);
        onCancel();
      }
    };
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [preview, seriesIds, onCancel]);

  const requestClose = useCallback(() => {
    switch (phase.kind) {
      case "loading":
      case "confirm":
        onCancel();
        return;
      case "result":
      case "unknown":
        onDone();
        return;
      case "running":
        return;
      default: {
        const unreachable: never = phase;
        throw new Error(`Unexpected phase: ${String(unreachable)}`);
      }
    }
  }, [phase, onCancel, onDone]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      // IME の変換中の Esc は変換の取り消しなので、モーダルを閉じない
      if (e.key !== "Escape" || e.isComposing || e.keyCode === 229) return;
      requestClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [requestClose]);

  const running = phase.kind === "running";
  useEffect(() => {
    if (!running) return undefined;
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [running]);

  const items = data?.items ?? [];
  const sendable = items.filter(
    item => item.eligibility === "ok" && item.template,
  );
  const sendCount = sendable.length;
  const hasBlank = sendable.some(item => {
    const draft = drafts[item.seriesId];
    return (
      !draft || draft.subject.trim() === "" || draft.bodyText.trim() === ""
    );
  });
  const totalContacts = data?.recipients.totalContacts ?? 0;
  const duplicatedRecipients = data?.recipients.duplicatedRecipients ?? 0;

  /**
   * 送れるシリーズを一括 API に送る.
   * @return {Promise<void>}
   */
  const handleSend = async (): Promise<void> => {
    if (!data) return;
    const requestItems = buildResendItems(data.items, drafts);
    setPhase({ kind: "running" });
    try {
      const response = await resend(requestItems);
      setPhase({ kind: "result", results: response.results });
      onSettled();
    } catch (e) {
      const failure = classifyBulkResendError(e);
      if (failure.kind === "unknown") {
        setPhase({ kind: "unknown" });
        onSettled();
        return;
      }
      setPhase({ kind: "confirm", failure });
    }
  };

  const isResultView = phase.kind === "result" || phase.kind === "unknown";
  const backdropCloses = phase.kind === "loading" || phase.kind === "confirm";

  return createPortal(
    <div className="modal modal-open">
      <div
        className="modal-box flex max-w-3xl flex-col overflow-hidden p-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 id={titleId} className="text-base font-bold">
            {isResultView
              ? "シリーズをまとめて配信の結果"
              : "シリーズをまとめて配信の確認"}
          </h3>
          <button
            type="button"
            aria-label="閉じる"
            className="text-base-content/60 hover:text-base-content disabled:opacity-40"
            onClick={requestClose}
            disabled={running}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {phase.kind === "loading" && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-base-content/60">
              <Loading size="sm" />
              確認内容を読み込んでいます…
            </div>
          )}
          {(phase.kind === "confirm" || phase.kind === "running") && (
            <div
              className={cn("space-y-3", running && "opacity-60")}
              aria-busy={running}
            >
              {phase.kind === "confirm" && phase.failure && (
                <div role="alert" className="alert alert-error text-sm">
                  <span>
                    {phase.failure.kind === "quota"
                      ? quotaMessage(phase.failure.remaining)
                      : REJECTED_MESSAGE}
                  </span>
                </div>
              )}
              {/* 下端の警告帯 (alert-warning) と重ならないよう、控えめな警告色の 1 行にする */}
              {duplicatedRecipients > 0 && (
                <p className="text-xs text-amber-700">
                  {duplicatedRecipientsNotice(duplicatedRecipients)}
                </p>
              )}
              <ul className="space-y-2">
                {items.map(item => (
                  <BulkResendSeriesItem
                    key={item.seriesId}
                    item={item}
                    draft={drafts[item.seriesId]}
                    readOnly={running}
                    onChange={next =>
                      setDrafts(prev => ({ ...prev, [item.seriesId]: next }))
                    }
                  />
                ))}
              </ul>
            </div>
          )}
          {phase.kind === "result" && <ResultView results={phase.results} />}
          {phase.kind === "unknown" && (
            <div role="alert" className="alert alert-error text-sm">
              <div>
                <p className="font-bold">{UNKNOWN_TITLE}</p>
                <p className="text-xs">{UNKNOWN_MESSAGE}</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-slate-200 px-6 py-4">
          {phase.kind === "confirm" && !phase.failure && sendCount > 0 && (
            <div className="alert alert-warning text-sm">
              <span>
                {sendCount} 件の配信を、合計 {totalContacts.toLocaleString()}{" "}
                件の宛先に今すぐ送信します。この操作は取り消せません。
              </span>
            </div>
          )}
          {running && (
            <div className="alert alert-info text-sm">
              <span className="inline-flex items-center gap-2">
                <Loading size="sm" />
                {sendCount}{" "}
                件の配信を開始しています。この画面を閉じずにお待ちください。
              </span>
            </div>
          )}
          <div className="flex justify-end gap-2">
            {phase.kind === "result" && (
              <Button type="button" onClick={onDone}>
                閉じる
              </Button>
            )}
            {phase.kind === "unknown" && (
              <>
                <Button type="button" variant="ghost" onClick={onDone}>
                  閉じる
                </Button>
                <Button asChild>
                  <Link
                    to="/deliveries"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    配信一覧を開く ↗
                  </Link>
                </Button>
              </>
            )}
            {!isResultView && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={requestClose}
                  disabled={running}
                >
                  キャンセル
                </Button>
                <Button
                  type="button"
                  onClick={handleSend}
                  disabled={
                    phase.kind !== "confirm" ||
                    phase.failure?.kind === "quota" ||
                    sendCount === 0 ||
                    hasBlank
                  }
                >
                  {running ? (
                    <Loading size="sm" />
                  ) : phase.kind === "confirm" && sendCount > 0 ? (
                    `${sendCount} 件を配信する`
                  ) : (
                    "配信する"
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop"
        data-testid="bulk-resend-backdrop"
        onClick={backdropCloses ? requestClose : undefined}
      />
    </div>,
    document.body,
  );
};

type ResultViewProps = {
  results: BulkResendResult[];
};

/**
 * 実行結果. 見出しとシリーズごとの ✓ / ✕ / … と理由.
 * @param {ResultViewProps} props 実行結果
 * @return {JSX.Element} 結果
 */
const ResultView: FC<ResultViewProps> = ({ results }) => {
  const allStarted = results.every(r => r.status === "started");
  const hasUnconfirmed = results.some(r => r.status === "unconfirmed");
  return (
    <div className="space-y-2">
      <div
        className={cn(
          "alert text-sm",
          allStarted ? "alert-success" : "alert-warning",
        )}
      >
        <div>
          <p className="font-bold">{resultHeadline(results)}</p>
          {allStarted && (
            <p className="text-xs">
              送信は順に進みます。送信の状況と反応は、各配信の詳細で確認できます。
            </p>
          )}
          {hasUnconfirmed && <p className="text-xs">{UNCONFIRMED_NOTE}</p>}
        </div>
      </div>
      <ul className="overflow-hidden rounded-lg border border-slate-200">
        {results.map(result => {
          const { kind, reason } = describeResult(result);
          return (
            <li
              key={result.seriesId}
              className="flex items-start gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0"
            >
              <span
                aria-hidden
                className={cn(
                  "w-4 flex-none text-center font-bold",
                  kind === "ok" && "text-emerald-700",
                  kind === "ng" && "text-rose-700",
                  kind === "hold" && "text-amber-700",
                )}
              >
                {kind === "ok" ? "✓" : kind === "ng" ? "✕" : "…"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{result.seriesName ?? "—"}</p>
                {kind === "ok" ? (
                  <p className="text-xs text-base-content/60">
                    {(result.totalRecipients ?? 0).toLocaleString()}{" "}
                    件の宛先に送信を開始しました。
                  </p>
                ) : (
                  <p
                    className={cn(
                      "text-xs",
                      kind === "ng" ? "text-rose-700" : "text-amber-700",
                    )}
                  >
                    {reason}
                  </p>
                )}
              </div>
              {result.deliveryId && (
                <Link
                  to={`/deliveries/${result.deliveryId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap text-xs text-primary hover:underline"
                >
                  配信詳細を開く ↗
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
